import { useIsFocused, useNavigationState } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListRenderItemInfo, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import type { PlayersDirectoryFilter, PlayersDirectoryItem } from '../../services/api';
import { directoryRequest } from '../../store/friends/friendsActions';
import { selectDirectory } from '../../store/friends/friendsReducer';
import { useSafeViewport } from '../../utils/hooks/useSafeViewport';
import ScaledBox from '../Components/common/ScaledBox';
import { IndicatorFlatList } from '../Components/common/ScrollIndicator';
import { getDesktopColumns } from '../Components/Desktop/DesktopBottomBars';
import { desktopScrollFade, useDesktopLayout } from '../Components/Desktop/desktopLayout';
import ProfileRedesignModal from '../Components/Modals/ProfileRedesignModal';
import FriendCard, { FRIEND_CARD_BASE_WIDTH } from '../Components/Social/FriendCard';
import {
	DEFAULT_FRIENDS_FILTER,
	FriendsFilterValues,
	isFriendsFilterActive,
	toDirectoryProfileFilter,
} from '../Components/Social/friendsFilter';
import FriendsFilterModal from '../Components/Social/FriendsFilterModal';
import FriendsFilterTabs, { FriendsFilter } from '../Components/Social/FriendsFilterTabs';
import FriendsSearchRow from '../Components/Social/FriendsSearchRow';
import { SOCIAL_FONT, socialColors } from '../Components/Social/socialTheme';
import { MainLayout } from '../Pages/MainLayout';
import { useBodyVisible } from './MainScreens';

// The search row, the filter tabs and the grid line up with the header: TopSection insets
// the avatar by 16 on the left and Nav insets the bell / menu by 16 on the right, so this
// column uses the same 16 and stretches with the viewport instead of being pinned to the
// design's 390-wide frame (which left it visibly narrower than the header on wider screens).
const CONTENT_MAX_WIDTH = 600;
const CONTENT_PADDING = 16;
const CARD_GAP = 8;
// Figma 'Friends Grid' (2111:24794): rows 109 apart for 100-high cards.
const CARD_ROW_GAP = 9;
const COLUMNS = 4;
// The search row and the filter tabs are fixed-height strips that stretch to their box: 46 and
// 34 high on mobile.
const SEARCH_HEIGHT = 46;
const TABS_HEIGHT = 34;
/** The desktop layer's mobile-to-desktop factor. */
const DESKTOP_FACTOR = 1.62;

/**
 * The grid is a virtualised list of rows: only the rows near the screen stay mounted, since every
 * card draws two SVGs and the list grows a page at a time. The next page loads a screen ahead.
 */
const GRID_LIST_WINDOW = {
	initialNumToRender: 6,
	maxToRenderPerBatch: 4,
	windowSize: 7,
	onEndReachedThreshold: 1,
} as const;

const rowKey = (row: PlayersDirectoryItem[]) => row[0].id;

const TAB_FILTERS: Record<FriendsFilter, PlayersDirectoryFilter> = {
	friends: 'FRIENDS',
	vip: 'VIP',
	online: 'ONLINE',
};

/** No tab selected lists every player of the casino; a tab narrows it, the search included. */
const serverFilterFor = (tab: FriendsFilter | null): PlayersDirectoryFilter =>
	tab ? TAB_FILTERS[tab] : 'ALL';

const chunk = (friends: PlayersDirectoryItem[], size: number) => {
	const rows: PlayersDirectoryItem[][] = [];
	for (let i = 0; i < friends.length; i += size) {
		rows.push(friends.slice(i, i + size));
	}
	return rows;
};

const FriendsScreen = () => {
	const isFocused = useIsFocused();
	const isBodyVisible = useBodyVisible();
	const activeRouteName = useNavigationState(
		state => state.routes[state.index ?? state.routes.length - 1]?.name,
	);
	const { t } = useTranslation();
	const { width } = useSafeViewport();
	const desktop = useDesktopLayout();
	const [query, setQuery] = useState('');
	const [filter, setFilter] = useState<FriendsFilter | null>(null);
	const [advanced, setAdvanced] = useState<FriendsFilterValues>(DEFAULT_FRIENDS_FILTER);
	const [filterOpen, setFilterOpen] = useState(false);
	const [selectedPlayer, setSelectedPlayer] = useState<PlayersDirectoryItem | null>(null);
	const dispatch = useDispatch();
	const directory = useSelector(selectDirectory);
	const serverFilter = serverFilterFor(filter);
	const profile = useMemo(() => toDirectoryProfileFilter(advanced), [advanced]);

	useEffect(() => {
		if (!isFocused) return;
		dispatch(directoryRequest({ filter: serverFilter, query, profile }));
	}, [dispatch, isFocused, profile, query, serverFilter]);

	const hasMore = directory.items.length < directory.totalCount;
	const loadMore = useCallback(() => {
		if (!hasMore || directory.loading || directory.loadingMore) return;
		dispatch(directoryRequest({ filter: serverFilter, query, profile, append: true }));
	}, [directory.loading, directory.loadingMore, dispatch, hasMore, profile, query, serverFilter]);

	const gridWidth = Math.min(width, CONTENT_MAX_WIDTH) - CONTENT_PADDING * 2;
	// Match the full-width search and filter controls at every mobile width.
	const cardWidth = (gridWidth - CARD_GAP * (COLUMNS - 1)) / COLUMNS;

	// On desktop the cards are drawn at the desktop factor and the grid takes as many columns as
	// the content width holds, instead of the mobile four.
	const k = desktop.scale * DESKTOP_FACTOR;
	const desktopCardWidth = FRIEND_CARD_BASE_WIDTH * k;
	const desktopGap = CARD_GAP * k;
	const columnCount = desktop.isDesktopLayout
		? Math.max(
				COLUMNS,
				Math.floor((desktop.contentWidth + desktopGap) / (desktopCardWidth + desktopGap)),
			)
		: COLUMNS;
	// Every row is as wide as a full one, so a short last row starts at the left edge
	// instead of being centred on its own.
	const desktopRowWidth = columnCount * desktopCardWidth + (columnCount - 1) * desktopGap;

	// The tab, the search and the filter popup are all applied by the server.
	const rows = useMemo(() => chunk(directory.items, columnCount), [columnCount, directory.items]);

	// The gap under each row is the row's own padding: a gap on the list's content would also be put
	// around the spacers the list draws in place of rows it has unmounted, shifting them.
	const rowCardWidth = desktop.isDesktopLayout ? desktopCardWidth : cardWidth;
	const rowStyle = useMemo(
		() =>
			desktop.isDesktopLayout
				? [styles.desktopGridRow, { columnGap: desktopGap, paddingBottom: CARD_ROW_GAP * k }]
				: [styles.gridRow, { paddingBottom: CARD_ROW_GAP }],
		[desktop.isDesktopLayout, desktopGap, k],
	);
	const renderRow = useCallback(
		({ item: row }: ListRenderItemInfo<PlayersDirectoryItem[]>) => (
			<View style={rowStyle}>
				{row.map(friend => (
					<FriendCard
						key={friend.id}
						friend={friend}
						width={rowCardWidth}
						onPress={setSelectedPlayer}
					/>
				))}
			</View>
		),
		[rowCardWidth, rowStyle],
	);

	const emptyText = (() => {
		if (rows.length) return null;
		if (directory.loading) {
			return t('T_SITE_FRIENDS_LOADING_PLAYERS', { defaultValue: 'Loading players...' });
		}
		if (directory.errorKey) return directory.errorKey;
		if (isFriendsFilterActive(advanced)) {
			return t('T_SITE_FRIENDS_NO_FILTER_MATCH', {
				defaultValue: 'No players match these filters.',
			});
		}
		if (filter === 'friends' && !query.trim()) {
			return t('T_SITE_FRIENDS_EMPTY', {
				defaultValue: 'No friends yet. Search for a player to send a friend request.',
			});
		}
		return t('T_SITE_FRIENDS_NO_PLAYERS', { defaultValue: 'No players found.' });
	})();

	// Friends stays under the overlays Home stays under (Settings, Profile, ... on desktop) and under
	// the Store everywhere. Other inactive screens still unmount to avoid colliding SVG fragment ids
	// when Friends is mounted again.
	if (!isBodyVisible && activeRouteName !== 'BuyPackages') return null;

	const filterModal = filterOpen ? (
		<FriendsFilterModal
			value={advanced}
			onClose={() => setFilterOpen(false)}
			onApply={next => {
				setAdvanced(next);
				setFilterOpen(false);
			}}
		/>
	) : null;

	const profileModal = selectedPlayer ? (
		<ProfileRedesignModal
			onClose={() => setSelectedPlayer(null)}
			player={{
				rank: 0,
				playerId: selectedPlayer.id,
				username: selectedPlayer.username ?? '',
				avatarFileId: selectedPlayer.avatarFileId,
				level: selectedPlayer.level,
				isVip: selectedPlayer.isVip,
				isFriend: selectedPlayer.friendshipStatus === 'FRIENDS',
				friendshipStatus: selectedPlayer.friendshipStatus,
				friendRequestId: selectedPlayer.friendRequestId,
			}}
		/>
	) : null;

	const emptyState = emptyText ? <Text style={styles.emptyText}>{emptyText}</Text> : null;

	if (desktop.isDesktopLayout) {
		// The desktop frame: the search row and the filter tabs share one row on the desktop
		// home's two columns, and the grid runs the full content width below them.
		const columns = getDesktopColumns(desktop.contentWidth, desktop.scale);

		return (
			<MainLayout>
				<View style={[styles.desktopPage, { paddingHorizontal: desktop.side }]}>
					<View
						style={[styles.desktopToolbar, { columnGap: columns.gap, marginTop: desktop.s(8) }]}
					>
						<ScaledBox width={columns.left / k} height={SEARCH_HEIGHT} scale={k}>
							<FriendsSearchRow
								value={query}
								onChangeText={setQuery}
								onPressFilter={() => setFilterOpen(true)}
							/>
						</ScaledBox>
						<ScaledBox width={columns.right / k} height={TABS_HEIGHT} scale={k}>
							<FriendsFilterTabs active={filter} onChange={setFilter} />
						</ScaledBox>
					</View>

					<IndicatorFlatList
						style={[styles.grid, { marginTop: desktop.s(20) }, desktopScrollFade(desktop.s(40))]}
						contentContainerStyle={[
							styles.gridContent,
							styles.desktopGridContent,
							{ paddingBottom: desktop.s(40) },
						]}
						showsVerticalScrollIndicator={false}
						data={rows}
						keyExtractor={rowKey}
						renderItem={renderRow}
						ListHeaderComponent={emptyState}
						onEndReached={loadMore}
						{...GRID_LIST_WINDOW}
					/>
				</View>
				{filterModal}
				{profileModal}
			</MainLayout>
		);
	}

	return (
		<MainLayout>
			<View style={styles.page}>
				<View style={styles.column}>
					<FriendsSearchRow
						value={query}
						onChangeText={setQuery}
						onPressFilter={() => setFilterOpen(true)}
					/>

					<View style={styles.tabs}>
						<FriendsFilterTabs active={filter} onChange={setFilter} />
					</View>

					{/* The four columns fill the whole column width, so the bar goes out in the page's
					    16 margin - x 382 on the 390 frame, where the design draws it - instead of
					    taking a gutter out of the grid, which pushed the first column off the left edge
					    as soon as the list grew tall enough to scroll. */}
					<IndicatorFlatList
						style={styles.grid}
						contentContainerStyle={styles.gridContent}
						showsVerticalScrollIndicator={false}
						reserveGutter={false}
						indicatorInsets={{ right: -(CONTENT_PADDING - 4) }}
						data={rows}
						keyExtractor={rowKey}
						renderItem={renderRow}
						ListHeaderComponent={emptyState}
						onEndReached={loadMore}
						{...GRID_LIST_WINDOW}
					/>
				</View>
			</View>
			{filterModal}
			{profileModal}
		</MainLayout>
	);
};

const styles = StyleSheet.create({
	page: {
		flex: 1,
		minHeight: 0,
		alignItems: 'center',
	},
	column: {
		flex: 1,
		minHeight: 0,
		width: '100%',
		maxWidth: CONTENT_MAX_WIDTH,
		paddingHorizontal: CONTENT_PADDING,
	},
	tabs: {
		marginTop: 16,
	},
	grid: {
		flex: 1,
		minHeight: 0,
		marginTop: 12,
	},
	gridContent: {
		paddingBottom: 16,
	},
	gridRow: {
		flexDirection: 'row',
		columnGap: CARD_GAP,
	},
	desktopPage: {
		...StyleSheet.absoluteFillObject,
	},
	desktopToolbar: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	desktopGridRow: {
		flexDirection: 'row',
	},
	desktopGridContent: {
		alignItems: 'center',
	},
	emptyText: {
		marginTop: 32,
		paddingHorizontal: 16,
		fontFamily: SOCIAL_FONT,
		fontSize: 14,
		fontWeight: '600',
		color: socialColors.placeholder,
		textAlign: 'center',
	},
});

export default FriendsScreen;
